import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { Icon } from './Icon';
import '../styles/templates.css';
import { RichText } from './RichText';

interface SigningInfo {
  contract: { hasBody: boolean; body: string | null; templateName: string | null; generatedAt: number | null };
  revision: { label: string | null; isCurrent: boolean; revisionOf: string | null; supersededById: string | null; family: Array<{ id: string; number: string; revisionLabel: string | null; isCurrent: boolean; status: string }> };
  signature: { id: string; token: string; url: string; status: string; signerName: string | null; signedAt: number | null; canDownload: boolean } | null;
}
interface Template { id: string; name: string; isDefault: boolean }

const SIG_LABEL: Record<string, string> = { pending: 'Pendiente de abrir', viewed: 'Abierto por el cliente', signed: 'Firmado', revoked: 'Revocado' };
const SIG_TONE: Record<string, string> = { pending: 'warn', viewed: 'warn', signed: 'active', revoked: 'broken' };

async function copy(text: string) { try { await navigator.clipboard.writeText(text); } catch { /* iframe */ } }

/**
 * Muestra el cuerpo del documento. El contenido nuevo llega como HTML (editor
 * rico); el antiguo, como texto plano. Se detecta y se representa cada uno como
 * corresponde, escapando el texto plano para evitar inyección.
 */
function renderBody(body: string | null): string {
  if (!body) return '';
  let s = body;
  if (!/</.test(s) && /&lt;/.test(s)) {
    s = s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
  }
  const looksHtml = /<(p|h[1-6]|ul|ol|li|strong|em|u|br|blockquote|table|thead|tbody|tr|td|th|hr|div|span|img|font|mark)[\s>]/i.test(s);
  if (looksHtml) return s;
  const escaped = s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped
    .split(/\n{2,}/)
    .map((para) => {
      const line = para.trim();
      const letters = line.replace(/[^A-Za-zÁÉÍÓÚÑÜáéíóúñü]/g, '');
      const isHeading = letters.length > 2 && letters === letters.toUpperCase() && line.length < 70;
      return isHeading
        ? `<h3>${line}</h3>`
        : `<p>${para.replace(/\n/g, '<br>')}</p>`;
    })
    .join('');
}

export function ContractPanel({ docId, docType = 'contract', docNumber = '' }: { docId: string; docType?: string; docNumber?: string }) {
  const nav = useNavigate();
  const isContract = docType === 'contract';
  const noun = isContract ? 'contrato' : 'quotation';
  const Noun = isContract ? 'Contrato' : 'Quotation';

  const [info, setInfo] = useState<SigningInfo | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [issued, setIssued] = useState<{ url: string; password: string } | null>(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const load = useCallback(() => {
    api.get<SigningInfo>(`/documents/${docId}/signing`).then(setInfo).catch(() => void 0);
  }, [docId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get<Template[]>(`/templates?docType=${docType}`).then(setTemplates).catch(() => void 0); }, [docType]);

  const generate = async (templateId?: string) => {
    setBusy('gen'); setErr(null);
    try { await api.post(`/documents/${docId}/generate-contract`, templateId ? { templateId } : {}); load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo generar.'); }
    finally { setBusy(null); }
  };

  const startEdit = () => { setDraft(info?.contract.body ?? ''); setEditing(true); };
  const saveEdit = async () => {
    setBusy('save'); setErr(null);
    try { await api.post(`/documents/${docId}/body`, { body: draft }); setEditing(false); load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo guardar el texto.'); }
    finally { setBusy(null); }
  };

  const sendToSign = async () => {
    setBusy('send'); setErr(null);
    try {
      const r = await api.post<{ url: string; password: string }>(`/documents/${docId}/signature-request`, {
        signerName: signerName.trim() || undefined,
        signerEmail: signerEmail.trim() || undefined,
      });
      setIssued({ url: r.url, password: r.password });
      setSendOpen(false);
      load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo generar el enlace.'); }
    finally { setBusy(null); }
  };

  const revoke = async () => {
    setBusy('revoke'); setErr(null);
    try { await api.post(`/documents/${docId}/signature-request/revoke`, {}); setIssued(null); load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo revocar.'); }
    finally { setBusy(null); }
  };

  const revise = async () => {
    setBusy('revise'); setErr(null);
    try { const r = await api.post<{ id: string }>(`/documents/${docId}/revise`, {}); nav(`/documentos/${r.id}`); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo revisar.'); }
    finally { setBusy(null); }
  };

  if (!info) return null;
  const fullUrl = (u: string) => `${window.location.origin}${import.meta.env.VITE_PREVIEW ? '/#' : ''}${u}`;
  const signed = info.signature?.status === 'signed';

  return (
    <div className="card" style={{ marginBottom: 4 }}>
      <div className="card__head">
        <span className="card__title">{isContract ? 'Contrato y firma digital' : 'Quotation text'}</span>
        {info.revision.label && <span className="tag tag--accent">Revision {info.revision.label}</span>}
      </div>
      <div className="card__body">
        <div className="contract-panel">
          <div className="contract-panel__bar">
            <button className="btn btn--ghost btn--sm" onClick={() => generate()} disabled={!!busy || editing}>
              <Icon name="spark" size={15} />{busy === 'gen' ? 'Redactando…' : info.contract.hasBody ? 'Regenerar con IA' : 'Generar con IA'}
            </button>
            {templates.length > 1 && (
              <select className="select select--sm" defaultValue="" onChange={(e) => { if (e.target.value) generate(e.target.value); }} disabled={!!busy || editing} style={{ maxWidth: 220 }}>
                <option value="">Elegir plantilla…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.isDefault ? ' (por defecto)' : ''}</option>)}
              </select>
            )}
            {info.contract.hasBody && !editing && (
              <button className="btn btn--ghost btn--sm" onClick={startEdit} disabled={!!busy}>
                <Icon name="sliders" size={15} />Editar texto
              </button>
            )}
            <div style={{ flex: 1 }} />
            {isContract && info.contract.hasBody && !signed && !editing && (
              <button className="btn btn--accent btn--sm" onClick={() => setSendOpen((v) => !v)} disabled={!!busy}>
                <Icon name="send" size={15} />Enviar a firma
              </button>
            )}
            {!editing && (
              <button className="btn btn--ghost btn--sm" onClick={revise} disabled={!!busy}>
                <Icon name="copy" size={15} />{busy === 'revise' ? 'Creando…' : 'Create revision'}
              </button>
            )}
          </div>

          {editing ? (
            <>
              <div style={{ marginTop: 4 }}>
                <RichText value={draft} onChange={setDraft} minHeight={340} />
              </div>
              <div className="row gap-10" style={{ marginTop: 10 }}>
                <button className="btn btn--primary btn--sm" onClick={saveEdit} disabled={busy === 'save'}>
                  <Icon name="check" size={15} />{busy === 'save' ? 'Guardando…' : 'Guardar cambios'}
                </button>
                <button className="btn btn--ghost btn--sm" onClick={() => setEditing(false)} disabled={busy === 'save'}>Cancelar</button>
                <span className="faint" style={{ fontSize: 12, alignSelf: 'center' }}>Puedes modificarlo parcial o completamente.</span>
              </div>
            </>
          ) : info.contract.hasBody ? (
            <div className="contract-preview" dangerouslySetInnerHTML={{ __html: renderBody(info.contract.body) }} />
          ) : (
            <div className="contract-preview__empty">
              <Icon name="file" size={26} style={{ opacity: 0.4 }} />
              <p style={{ marginTop: 8 }}>No {noun} drafted yet. Press "Generate with AI" to create it from a template, or edit it by hand.</p>
            </div>
          )}
        </div>

        {err && <div className="act-error" style={{ marginTop: 12 }}><Icon name="alert" size={16} />{err}</div>}

        {/* Firma (solo contratos) */}
        {isContract && sendOpen && (
          <div className="link-box">
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Enviar a firma</div>
            <div className="row gap-12 wrap">
              <div className="field grow" style={{ minWidth: 180, marginBottom: 0 }}>
                <label>Nombre del firmante (opcional)</label>
                <input className="input" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
              </div>
              <div className="field grow" style={{ minWidth: 180, marginBottom: 0 }}>
                <label>Email (opcional)</label>
                <input className="input" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} placeholder="cliente@empresa.com" />
              </div>
            </div>
            <button className="btn btn--primary" style={{ marginTop: 12 }} onClick={sendToSign} disabled={busy === 'send'}>
              {busy === 'send' ? 'Generando enlace…' : 'Generate link and password'}
            </button>
          </div>
        )}

        {isContract && issued && (
          <div className="link-box" style={{ borderColor: 'var(--accent)' }}>
            <div className="row center gap-8" style={{ color: 'var(--accent-deep)', fontWeight: 600 }}>
              <Icon name="check" size={16} />Link generated — send it to the client along with the password
            </div>
            <div className="link-row">
              <span className="link-code">{fullUrl(issued.url)}</span>
              <button className="icon-btn" title="Copiar enlace" onClick={() => copy(fullUrl(issued.url))}><Icon name="copy" size={16} /></button>
              <button className="btn btn--ghost btn--sm" onClick={() => nav(issued.url)}><Icon name="external" size={14} />Abrir</button>
            </div>
            <div className="row center gap-10" style={{ marginTop: 12 }}>
              <span className="faint" style={{ fontSize: 12.5 }}>Password:</span>
              <span className="pw-badge">{issued.password}</span>
              <button className="icon-btn" title="Copy password" onClick={() => copy(issued.password)}><Icon name="copy" size={16} /></button>
            </div>
            <p className="faint" style={{ fontSize: 11.5, marginTop: 10 }}>For security, the password is only shown now. Save it before closing.</p>
          </div>
        )}

        {isContract && info.signature && !issued && (
          <div className="link-box">
            <div className="row between center">
              <div className="row center gap-10">
                <span className={`pill pill--${SIG_TONE[info.signature.status] ?? 'muted'}`}><span className="pill__dot" />{SIG_LABEL[info.signature.status] ?? info.signature.status}</span>
                {info.signature.signerName && <span className="faint" style={{ fontSize: 12.5 }}>por {info.signature.signerName}</span>}
                {info.signature.signedAt && <span className="faint" style={{ fontSize: 12.5 }}>· {new Date(info.signature.signedAt).toLocaleDateString('es-ES')}</span>}
              </div>
              {!signed && info.signature.status !== 'revoked' && (
                <button className="btn btn--ghost btn--sm" onClick={revoke} disabled={!!busy}>Revocar enlace</button>
              )}
            </div>
            <div className="link-row">
              <span className="link-code">{fullUrl(info.signature.url)}</span>
              <button className="icon-btn" title="Copiar enlace" onClick={() => copy(fullUrl(info.signature!.url))}><Icon name="copy" size={16} /></button>
            </div>
          </div>
        )}

        {/* Revisiones (ambos tipos) */}
        {info.revision.family.length > 1 && (
          <div style={{ marginTop: 16 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Revisiones de {docNumber ? `${docNumber}` : `esta ${noun}`}</div>
            {info.revision.family.map((r) => (
              <div key={r.id} className="family-row">
                <button className="row center gap-8" onClick={() => nav(`/documentos/${r.id}`)} style={{ textAlign: 'left' }}>
                  <Icon name="file" size={15} style={{ color: r.isCurrent ? 'var(--accent)' : 'var(--mist)' }} />
                  <span style={{ fontWeight: r.isCurrent ? 600 : 400 }}>{r.number}</span>
                  {r.isCurrent && <span className="tag tag--accent">vigente</span>}
                </button>
                <span className="faint" style={{ fontSize: 12 }}>{r.status}</span>
              </div>
            ))}
          </div>
        )}

        {!isContract && <p className="faint" style={{ fontSize: 12, marginTop: 14 }}>{Noun} ready to share with the client from the "Share" button above.</p>}
      </div>
    </div>
  );
}
